import type { CSSProperties, HTMLAttributes } from 'react';
import { Image as ImageIcon, LayoutTemplate } from 'lucide-react';
import { clsx } from '../clsx';
import { Icon } from './Icon';
import styles from './ImageSlot.module.css';

export interface ImageSlotProps extends HTMLAttributes<HTMLDivElement> {
  src?: string | undefined;
  /** Responsive candidates (`<url> 400w, …`) with the `sizes` the slot is laid out at. */
  srcSet?: string | undefined;
  sizes?: string | undefined;
  /** `lazy` for pictures below the fold; the hero stays `eager` with `fetchPriority="high"`. */
  loading?: 'eager' | 'lazy' | undefined;
  fetchPriority?: 'high' | 'low' | 'auto' | undefined;
  /** Alternative text of the image; also announced for the placeholder. */
  alt: string;
  /** Caption of the empty frame ("Objektbild · Quartier Lindenhof", "Grundriss folgt"). */
  placeholder?: string | undefined;
  /** `photo` shows the picture icon, `plan` the floor-plan icon. */
  kind?: 'photo' | 'plan' | undefined;
  /** CSS aspect ratio when no height is set ("3 / 2" default). */
  ratio?: string | undefined;
  height?: number | undefined;
  fit?: 'cover' | 'contain' | undefined;
  /** `media` (10 px), `card` (14 px), `none` (inside a clipped card). */
  radius?: 'media' | 'card' | 'none' | undefined;
  /**
   * `ds` (default): 24 px icon, ring 4 px inside the frame. `frame`: the empty state exactly as the screens'
   * `<image-slot>` draws it — its single 28 px picture icon at 45 %, ring on the edge, medium caption
   * (Objekte 01–06); `kind` only picks the icon of the `ds` chrome.
   */
  chrome?: 'ds' | 'frame' | undefined;
}

/** Image or placeholder frame for object photos and floor plans (the `<image-slot>` of the screens). */
export function ImageSlot({
  src,
  srcSet,
  sizes,
  loading,
  fetchPriority,
  alt,
  placeholder,
  kind = 'photo',
  ratio = '3 / 2',
  height,
  fit = 'cover',
  radius = 'media',
  chrome = 'ds',
  className,
  style,
  ...rest
}: ImageSlotProps) {
  const size: CSSProperties = height !== undefined ? { height } : { aspectRatio: ratio };
  return (
    <div
      className={clsx(
        styles.slot,
        radius === 'card' && styles.radiusCard,
        radius === 'none' && styles.radiusNone,
        className,
      )}
      style={{ ...size, ...style }}
      {...rest}
    >
      {src ? (
        <img
          className={clsx(styles.image, fit === 'contain' && styles.contain)}
          src={src}
          srcSet={srcSet}
          sizes={srcSet ? sizes : undefined}
          alt={alt}
          loading={loading}
          fetchPriority={fetchPriority}
          decoding={fetchPriority === 'high' ? undefined : 'async'}
        />
      ) : (
        <div
          className={clsx(styles.placeholder, chrome === 'frame' && styles.frameChrome)}
          role="img"
          aria-label={placeholder ?? alt}
        >
          <span className={styles.ring} aria-hidden="true" />
          {chrome === 'frame' ? (
            <svg
              className={styles.frameIcon}
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="m21 15-5-5L5 21" />
            </svg>
          ) : (
            <Icon icon={kind === 'plan' ? LayoutTemplate : ImageIcon} size={24} />
          )}
          {placeholder ? <span className={styles.caption}>{placeholder}</span> : null}
        </div>
      )}
    </div>
  );
}
